import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { motion, useScroll, useMotionValueEvent } from 'framer-motion';
import type { AdminTab } from '../lib/rolePermissions';

interface Tournament {
  id: string;
  name: string;
  date: string;
  status: string;
}

interface DashboardHeaderProps {
  tournaments: Tournament[];
  selectedTournament: Tournament;
  onTournamentChange: (tournament: Tournament) => void;
  activeTab: string;
  onTabChange: (tab: string) => void;
  allowedTabs?: AdminTab[];
  tabsLoading?: boolean;
}

const NAV_TABS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'players', label: 'Players' },
  { key: 'partners', label: 'Partners' },
  { key: 'calendar', label: 'Calendar' },
  { key: 'finance', label: 'Finance' },
  { key: 'contents', label: 'Contents' },
  { key: 'tournaments', label: 'Tournaments' },
  { key: 'courses', label: 'Courses' },
  { key: 'press', label: 'Press' },
  { key: 'analytics', label: 'Analytics' },
  { key: 'messaging', label: 'Messaging' },
  { key: 'interest', label: 'Invites' },
  { key: 'settings', label: 'Settings' },
];

export default function DashboardHeader({
  tournaments,
  selectedTournament,
  onTournamentChange,
  activeTab,
  onTabChange,
  allowedTabs,
  tabsLoading = false,
}: DashboardHeaderProps) {
  const visibleTabs = tabsLoading
    ? []
    : allowedTabs
      ? NAV_TABS.filter(t => allowedTabs.includes(t.key as AdminTab))
      : NAV_TABS;
  const [showTournamentDropdown, setShowTournamentDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { scrollY } = useScroll();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useMotionValueEvent(scrollY, "change", (latest) => {
    setIsScrolled(latest > 0);
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowTournamentDropdown(false);
      }
    };
    if (showTournamentDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTournamentDropdown]);

  const getStatusBadge = (status: string) => {
    const normalized = status.toLowerCase();
    if (normalized === 'upcoming' || normalized === 'active') {
      return 'bg-green-100 text-green-800';
    }
    if (normalized === 'completed') {
      return 'bg-gray-100 text-gray-800';
    }
    return 'bg-yellow-100 text-yellow-800';
  };

  return (
    <motion.div
      className="bg-white shadow fixed left-0 right-0 z-40"
      initial={{ top: '5rem' }}
      animate={{
        top: isScrolled ? 0 : '5rem',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
      }}
      transition={{
        duration: 0.3,
        ease: "easeInOut"
      }}
      style={{
        backdropFilter: 'blur(8px)',
        borderBottom: '1px solid rgba(229, 231, 235, 0.5)'
      }}
    >
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-4">
            <h1 className="text-xl font-display font-bold text-golf-gray hidden lg:block">
              Admin Portal
            </h1>
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowTournamentDropdown(!showTournamentDropdown)}
                className="flex items-center space-x-2 px-4 py-2 bg-golf-sand rounded-lg hover:bg-golf-sand/80 transition-colors"
              >
                <span className="text-golf-gray font-medium hidden sm:inline truncate max-w-[160px]">{selectedTournament.name}</span>
                <span className="text-golf-gray font-medium sm:hidden">Tournament</span>
                <svg
                  className={`w-5 h-5 text-golf-gray transition-transform flex-shrink-0 ${
                    showTournamentDropdown ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {showTournamentDropdown && (
                <div className="absolute z-50 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200">
                  <div className="py-1">
                    {tournaments.map((tournament) => (
                      <button
                        key={tournament.id}
                        onClick={() => {
                          onTournamentChange(tournament);
                          setShowTournamentDropdown(false);
                        }}
                        className={`w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center justify-between ${
                          selectedTournament.id === tournament.id
                            ? 'bg-golf-sand/30'
                            : ''
                        }`}
                      >
                        <div>
                          <div className="font-medium text-golf-gray">
                            {tournament.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {tournament.date}
                          </div>
                        </div>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${getStatusBadge(tournament.status)}`}>
                          {tournament.status}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="hidden md:flex items-center overflow-x-auto scrollbar-hide">
            <div className="flex items-center space-x-1">
              {visibleTabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => onTabChange(tab.key)}
                  className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                    activeTab === tab.key
                      ? 'bg-golf-orange text-white'
                      : 'text-gray-500 hover:text-golf-gray'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <Link
              to="/"
              className="modern-button-outline px-2.5 py-1 text-xs hidden sm:block"
            >
              Back to Site
            </Link>

            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              {showMobileMenu ? (
                <XMarkIcon className="w-6 h-6 text-gray-600" />
              ) : (
                <Bars3Icon className="w-6 h-6 text-gray-600" />
              )}
            </button>
          </div>
        </div>
      </div>

      {showMobileMenu && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <div className="px-4 py-2 space-y-1">
            {visibleTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  onTabChange(tab.key);
                  setShowMobileMenu(false);
                }}
                className={`w-full px-4 py-2 text-left text-sm font-medium rounded-lg ${
                  activeTab === tab.key
                    ? 'bg-golf-orange text-white'
                    : 'text-gray-500 hover:text-golf-gray'
                }`}
              >
                {tab.label}
              </button>
            ))}
            <Link
              to="/"
              className="block w-full px-4 py-2 text-left text-sm font-medium text-gray-500 hover:text-golf-gray rounded-lg sm:hidden"
            >
              Back to Site
            </Link>
          </div>
        </div>
      )}
    </motion.div>
  );
}
